import { makeAutoObservable, reaction, runInAction } from "mobx";

import { createChat, disposeApi, getApiErrorMessage, listDocumentChats } from "@/api";
import { ChatModel } from "@/models/chat/ChatModel";
import { SCREEN_SM } from "@/style/breakpoints";

import type { Chat, ChatKind, CreateChatBodyKind } from "@/api";
import type { RootStore } from "@/store/RootStore";

export type ChatsStatus = "idle" | "loading" | "loaded" | "error";

export type CreatingChatStatus = "idle" | "creating" | "created" | "error";

const CHAT_WINDOW_INITIAL_WIDTH = 520;
export const CHAT_WINDOW_MAX_WIDTH = 1024;
export const CHAT_WINDOW_MIN_WIDTH = 320;
const CHAT_WINDOW_WIDTH_LS_KEY = "chatWindowWidth";

export function getLatestChat(chats: ChatModel[]): ChatModel | undefined {
  return chats.sort((a, b) => {
    const aCreatedAt = a.messages[0]?.createdAt || "";
    const bCreatedAt = b.messages[0]?.createdAt || "";
    return new Date(bCreatedAt).getTime() - new Date(aCreatedAt).getTime();
  })[0];
}

export class ChatsStore {
  private rootStore: RootStore;

  private chatsMap = new Map<string, ChatModel>();

  private fetchChatsDispose?: () => void;

  private openChatDispose?: () => void;

  private ensureBotChatDispose?: () => void;

  private docIdReactionDispose: () => void;

  private docFetchReactionDispose: () => void;

  private _isOpenChat = false;

  private lastActiveChatWithBotForDocId = new Map<string, string>(); // docId -> chatUid

  private lastActiveChatWithTeamForDocId = new Map<string, string>(); // docId -> chatUid

  private ensureBotChatPromise?: Promise<void>;

  private fetchChatsPromise?: Promise<void>;

  activeChatUid: string = "";

  activeChatKind: ChatKind | null = null;

  chatWindowWidth: number = CHAT_WINDOW_INITIAL_WIDTH;

  status: ChatsStatus = "idle";

  creatingChatStatus: CreatingChatStatus = "idle";

  error = "";

  creatingChatError = "";

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    if (typeof window !== "undefined") {
      const lastActiveChatWithBotForDocId = localStorage.getItem("lastActiveChatWithBotForDocId");
      if (lastActiveChatWithBotForDocId) {
        this.lastActiveChatWithBotForDocId = new Map(
          JSON.parse(lastActiveChatWithBotForDocId) as [string, string][],
        );
      }

      const lastActiveChatWithTeamForDocId = localStorage.getItem("lastActiveChatWithTeamForDocId");
      if (lastActiveChatWithTeamForDocId) {
        this.lastActiveChatWithTeamForDocId = new Map(
          JSON.parse(lastActiveChatWithTeamForDocId) as [string, string][],
        );
      }

      this.chatWindowWidth =
        Number(localStorage.getItem(CHAT_WINDOW_WIDTH_LS_KEY)) || CHAT_WINDOW_INITIAL_WIDTH;
    }

    makeAutoObservable(this);

    this.docIdReactionDispose = reaction(
      () => this.doc?.id,
      (docId) => {
        this.disposeChats((chat) => chat.docId !== docId);
        this.ensureBotChatPromise = undefined;
        this.fetchChatsPromise = undefined;
        if (this.isOpenChat) {
          this.openChat(this.activeChatKind === "personal");
        }
      },
    );

    this.docFetchReactionDispose = reaction(
      () => (this.doc?.api.isFetched ? this.doc.id : undefined),
      (docId) => {
        if (!docId || typeof window === "undefined" || window.innerWidth < SCREEN_SM) return;
        if (this.doc?.unreadMessagesCount) {
          this.openUnreadChat();
        }
      },
      { fireImmediately: true },
    );
  }

  // Students in classroom teams have no bot chat (it reads the whole team). UI-only gating, see docs/plans/student-permissions.md.
  get botChatDisabled() {
    return this.rootStore.classroom.isStudentIn(this.doc?.team);
  }

  get canOpenTeamChat() {
    return !!this.doc?.permissions.comment;
  }

  private disposeChats(predicate: (chat: ChatModel) => boolean = () => true) {
    for (const [uid, chat] of this.chatsMap) {
      if (predicate(chat)) {
        chat.dispose();
        this.chatsMap.delete(uid);
      }
    }
  }

  get doc() {
    return this.rootStore.doc;
  }

  get activeChat() {
    return this.chats.find((c) => c.uid === this.activeChatUid);
  }

  setStatus(status: ChatsStatus) {
    this.status = status;
  }

  setCreatingChatStatus(status: CreatingChatStatus) {
    this.creatingChatStatus = status;
  }

  setError(error: string) {
    this.error = error;
  }

  setCreatingChatError(error: string) {
    this.creatingChatError = error;
  }

  setActiveChatKind(kind: ChatKind | null) {
    this.activeChatKind = kind;
  }

  setChatWindowWidth(width: number, persist: boolean = false) {
    this.chatWindowWidth = width;
    if (!persist) return;

    localStorage.setItem(CHAT_WINDOW_WIDTH_LS_KEY, String(width));
    this.rootStore.view.syncChatLayout();
  }

  async openChatByUid(chatUid: string, messageUid?: string) {
    await this.fetchChats();
    const target = this.chats.find((chat) => chat.uid === chatUid);
    if (!target) return;
    target.setPendingScrollToMessageUid(messageUid);
    this.openChat(target.withBot, chatUid);
  }

  setLastActiveChatWithBotForDocId(docId: string, chatUid: string) {
    this.lastActiveChatWithBotForDocId.set(docId, chatUid);
    localStorage.setItem(
      "lastActiveChatWithBotForDocId",
      JSON.stringify(Array.from(this.lastActiveChatWithBotForDocId.entries())),
    );
  }

  setLastActiveChatWithTeamForDocId(docId: string, chatUid: string) {
    this.lastActiveChatWithTeamForDocId.set(docId, chatUid);
    localStorage.setItem(
      "lastActiveChatWithTeamForDocId",
      JSON.stringify(Array.from(this.lastActiveChatWithTeamForDocId.entries())),
    );
  }

  openChat(withBot: boolean = false, chatUid?: string, options: { markRead?: boolean } = {}) {
    if (withBot && this.botChatDisabled) {
      if (this.isOpenChat) {
        this.closeChat();
      }
      return;
    }

    // `markRead: false` warms the chat (fetch + subscribe) WITHOUT treating it as
    // opened — used by the mobile shell to preload a hidden chat WebView. The
    // chat is only marked read once the sheet is actually visible (the native
    // side then flips it via `setIsOpened`/`markAsRead`). Defaults to true, so
    // every other caller (desktop) is unchanged.
    const markRead = options.markRead ?? true;
    this.openChatDispose?.();
    this.openChatDispose = disposeApi(async (signal) => {
      this.activeChat?.setIsOpened(false);
      this.setActiveChatKind(withBot ? "personal" : "collective");
      this.setError("");
      this.setCreatingChatError("");

      runInAction(() => {
        this.activeChatUid = "";
        this._isOpenChat = true;
      });
      this.rootStore.view.syncChatLayout();

      if (this.chats.length === 0) {
        await this.fetchChats(signal);
      }
      if (signal?.aborted) return;

      if (withBot) {
        if (this.chatsWithBot.length === 0) {
          return this.createChat("personal");
        }
        runInAction(() => {
          const lateChatWithBot = getLatestChat(this.chatsWithBot);
          const currentDocId = this.doc?.id || "";
          const targetChatUid =
            chatUid || this.lastActiveChatWithBotForDocId.get(currentDocId) || "";
          const targetChat = this.chatsWithBot.find((chat) => chat.uid === targetChatUid);
          this.activeChatUid = targetChat?.uid || lateChatWithBot!.uid;
          this.setLastActiveChatWithBotForDocId(currentDocId, this.activeChatUid);
        });
      } else if (!withBot) {
        if (this.chatsWithTeam.length === 0) {
          return this.createChat("collective");
        }
        runInAction(() => {
          const currentDocId = this.doc?.id || "";
          const targetChatUid =
            chatUid || this.lastActiveChatWithTeamForDocId.get(currentDocId) || "";
          const targetChat = this.chatsWithTeam.find((chat) => chat.uid === targetChatUid);
          this.activeChatUid = targetChat?.uid || this.chatsWithTeam[0].uid;
          this.setLastActiveChatWithTeamForDocId(currentDocId, this.activeChatUid);
        });
      }

      this.activeChat?.setIsOpened(markRead);
      await this.activeChat?.fetchMessages();
      if (markRead) this.activeChat?.markAsRead();
    });
  }

  closeChat(focusEditor = true) {
    if (this.isOpenChat) {
      this._isOpenChat = false;
      this.setError("");
      this.setCreatingChatError("");
      this.setActiveChatKind(null);
      this.rootStore.view.syncChatLayout();
      this.activeChat?.setIsOpened(false);
      this.activeChatUid = "";
    }

    if (focusEditor) {
      this.doc?.focus();
    }
  }

  ensureBotChatForCurrentDoc(): Promise<void> {
    if (this.botChatDisabled || !this.doc?.permissions.comment) return Promise.resolve();
    if (this.chatsWithBot.length > 0) return Promise.resolve();
    if (this.ensureBotChatPromise) return this.ensureBotChatPromise;

    let promise!: Promise<void>;
    this.ensureBotChatDispose?.();
    this.ensureBotChatDispose = disposeApi((signal) => {
      promise = (async () => {
        if (this.chatsWithBot.length === 0) {
          await this.fetchChats(signal);
        }
        if (this.chatsWithBot.length === 0) {
          await this.createChat("personal", { activate: false, signal });
        }
      })();
      return promise;
    });

    this.ensureBotChatPromise = promise;
    promise.finally(() => {
      if (this.ensureBotChatPromise === promise) {
        this.ensureBotChatPromise = undefined;
      }
    });

    return promise;
  }

  get isOpenChat() {
    return this._isOpenChat;
  }

  get isFloatingInputShown() {
    const { view } = this.rootStore;
    return (
      !this.botChatDisabled &&
      !this.isOpenChat &&
      this.chatsWithBot.length > 0 &&
      !view.areSidebarsHidden &&
      !view.isFloatingChatInputHidden
    );
  }

  async openUnreadChat() {
    if (this.isOpenChat) return;
    const docId = this.doc?.id;

    await this.fetchChats();
    if (this.isOpenChat || this.doc?.id !== docId) return;

    const unreadChat = this.chats.find((chat) => chat.unreadMessagesCount > 0);
    if (unreadChat) {
      this.openChat(unreadChat.withBot, unreadChat.uid);
    }
  }

  fetchChats(signal?: AbortSignal): Promise<void> {
    if (this.fetchChatsPromise) return this.fetchChatsPromise;
    const uid = this.doc?.id;
    if (!uid) return Promise.resolve();

    const promise = (async () => {
      try {
        this.setStatus("loading");
        this.setError("");
        const result = await listDocumentChats(uid, { signal });
        if (signal?.aborted) return;
        if (result.chats) {
          this.updateChats(result.chats, uid);
        }
        this.setStatus("loaded");
      } catch (err) {
        this.setError(getApiErrorMessage(err));
        this.setStatus("error");
      }
    })();

    this.fetchChatsPromise = promise;
    promise.finally(() => {
      if (this.fetchChatsPromise === promise) {
        this.fetchChatsPromise = undefined;
      }
    });
    return promise;
  }

  fetchChatsWithDispose() {
    this.fetchChatsDispose?.();
    this.fetchChatsDispose = disposeApi(async (signal) => {
      await this.fetchChats(signal);
    });
  }

  async createChat(
    kind: CreateChatBodyKind,
    { signal, activate = true }: { signal?: AbortSignal; activate?: boolean } = {},
  ) {
    const uid = this.doc?.id;
    if (!uid) return;
    try {
      this.setCreatingChatStatus("creating");
      this.setCreatingChatError("");
      const { chat } = await createChat({ uid, kind }, { signal });
      if (signal?.aborted) return;
      if (chat) {
        if (activate) {
          this.activeChat?.setIsOpened(false);
          runInAction(() => {
            this.activeChatUid = chat.uid;
          });
        }
        if (kind === "personal") {
          this.setLastActiveChatWithBotForDocId(uid, chat.uid);
        } else {
          this.setLastActiveChatWithTeamForDocId(uid, chat.uid);
        }
        this.updateChats([chat], uid);
        if (activate) {
          this.activeChat?.setIsOpened(true);
        }
      }
      this.setCreatingChatStatus("created");
    } catch (err) {
      this.setCreatingChatError(getApiErrorMessage(err));
      this.setCreatingChatStatus("error");
    }
  }

  private updateChats(chats: Chat[], docId: string) {
    chats.forEach((c) => {
      if (!this.chatsMap.has(c.uid)) {
        const model = new ChatModel(c, docId, this.rootStore.auth.currentUser, this);
        this.chatsMap.set(c.uid, model);
        model.subscribe();
      }
    });
  }

  get chats() {
    const currentDocId = this.doc?.id;
    if (!currentDocId) return [];
    return [...this.chatsMap.values()].filter((chat) => chat.docId === currentDocId);
  }

  get chatsWithBot() {
    const currentDocId = this.doc?.id;
    if (!currentDocId) return [];
    return [...this.chatsMap.values()].filter(
      (chat) => chat.docId === currentDocId && chat.withBot,
    );
  }

  get isBotGeneratingForCurrentDoc() {
    return this.chatsWithBot.some((chat) => chat.isBotResponding);
  }

  get chatsWithTeam() {
    const currentDocId = this.doc?.id;
    if (!currentDocId) return [];
    return [...this.chatsMap.values()].filter(
      (chat) => chat.docId === currentDocId && !chat.withBot,
    );
  }

  get totalMessagesCountForTeamChat() {
    return this.chatsWithTeam.reduce((acc, chat) => acc + chat.messagesCount, 0);
  }

  get unreadMessagesCountForTeamChat() {
    return this.chatsWithTeam.reduce((acc, chat) => acc + chat.unreadMessagesCount, 0);
  }

  get unreadMessagesCountForBotChat() {
    return this.chatsWithBot.reduce((acc, chat) => acc + chat.unreadMessagesCount, 0);
  }

  dispose() {
    this.disposeChats();
    this.openChatDispose?.();
    this.fetchChatsDispose?.();
    this.ensureBotChatDispose?.();
    this.docIdReactionDispose();
    this.docFetchReactionDispose();
  }
}
